import type { QueryClient } from '@tanstack/react-query';
import { conversationQueryOptions } from './conversation-query.util';

/**
 * Identifies the temporary recovery owned by one account's canonical detail query.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant whose response can satisfy this recovery.
 * @returns Child key cancelled alongside ordinary conversation reads.
 */
export const conversationRecoveryQueryKey = (
  conversationId: number,
  tenantId: string
) => [
  ...conversationQueryOptions(conversationId).queryKey,
  'recovery',
  tenantId,
];

/**
 * Retains active recovery authority independently of bounded socket-history eviction.
 * Settled, cancelled, removed and expired queries no longer contribute authority.
 * @param queryClient - Account-owned cache containing temporary recovery work.
 * @param conversationId - Authorized CRM conversation identity.
 * @param tenantId - Tenant whose active recovery must be protected.
 * @returns Minimum revision timestamp, or zero when no bounded recovery is active.
 */
export const getConversationRecoveryMinimum = (
  queryClient: QueryClient,
  conversationId: number,
  tenantId: string
): number => {
  const recovery = queryClient.getQueryCache().find({
    queryKey: conversationRecoveryQueryKey(conversationId, tenantId),
    exact: true,
  });
  const minimum = recovery?.meta?.minimumRevision;
  const deadline = recovery?.meta?.deadline;
  return recovery?.state.fetchStatus !== 'idle' &&
    typeof minimum === 'number' &&
    Number.isFinite(minimum) &&
    typeof deadline === 'number' &&
    Date.now() < deadline
    ? minimum
    : 0;
};
